import type { CountryItem } from '@/types/countries'
import { VpBadge, VpSearch } from '@vtmn-play/react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Loading } from '@/components/loading/Loading'
import { Modal } from '@/components/Modal'
import { useCountrySelection } from '@/hooks/useCountrySelection'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'
import { CountrySelectorModalList } from './CountrySelectorModalList'
import './country-selector-modal.scss'

interface CountrySelectorModalProps {
  countries: CountryItem[]
  hasCountriesError: boolean
  isOpen: boolean
  isLoadingCountries: boolean
  onClose: () => void
}

export function CountrySelectorModal({
  countries,
  hasCountriesError,
  isOpen,
  isLoadingCountries,
  onClose,
}: Readonly<CountrySelectorModalProps>) {
  const countrySearchInputId = 'country-selector-search-input'
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentSelectionKey = searchParams.getAll('homologation_countries')
    .map(code => code.trim().toUpperCase())
    .join('|')
  const stableSelectedIds = useMemo(
    () => {
      const selectedCodes = new Set(currentSelectionKey.split('|').filter(Boolean).map(code => code.toUpperCase()))
      return countries
        .filter(country => selectedCodes.has(country.code.toUpperCase()))
        .map(country => country.id)
    },
    [countries, currentSelectionKey],
  )

  const {
    searchQuery,
    setSearchQuery,
    filteredCountries,
    handleToggle,
    handleToggleAll,
    checked,
    isValid,
    tempSelected,
    resetToCurrentSelection,
    applySelection,
  } = useCountrySelection(stableSelectedIds, countries)

  useEffect(() => {
    if (isOpen && countries.length > 0) {
      resetToCurrentSelection(stableSelectedIds)
    }
  }, [countries, isOpen, resetToCurrentSelection, stableSelectedIds])

  const handleClose = () => {
    resetToCurrentSelection(stableSelectedIds)
    onClose()
  }

  const handleValidate = () => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.COUNTRY_SELECTION_VALIDATED, {
      selected_countries_count: tempSelected.length,
    })
    applySelection(setSearchParams)
    onClose()
  }

  const titleLabel = countries.length > 0 && tempSelected.length === countries.length
    ? t('products-page.all-countries')
    : (
        <span className="country-selector-modal-title">
          {t('products-page.countries-selected')}
          {' '}
          <VpBadge>{tempSelected.length}</VpBadge>
        </span>
      )

  return (
    <Modal
      dataTestid="country-selector-modal"
      isOpen={isOpen}
      title={titleLabel}
      width="480px"
      validationDisabled={isLoadingCountries || hasCountriesError || !isValid}
      validationAction={handleValidate}
      cancelAction={handleClose}
      onClose={handleClose}
    >
      <div className="country-selector-modal-content">
        {isLoadingCountries
          ? <Loading loadingLabel={t('products-page.country-modal.loading')} />
          : (
              <>
                <div className="country-search-bar" data-testid="country-search-bar">
                  <label className="sr-only" htmlFor={`search-input-${countrySearchInputId}`}>
                    {t('products-page.country-search-placeholder')}
                  </label>
                  <VpSearch
                    id={countrySearchInputId}
                    placeholder={t('products-page.country-search-placeholder')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <CountrySelectorModalList
                  countries={countries}
                  filteredCountries={filteredCountries}
                  hasCountriesError={hasCountriesError}
                  handleToggle={handleToggle}
                  handleToggleAll={handleToggleAll}
                  checked={checked}
                />
              </>
            )}
      </div>
    </Modal>
  )
}
